import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { partition, map, debounce, includes, isEmpty } from 'lodash';
import LocationSvg from '@src/svgs/fr-location.svg';
import apolloClient from '@src/graphql/apollo';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { idify } from '@src/util/idify';
import { GetOpeningReachDocument } from '@src/graphql/generated';
import Reach from './Reach';
import Item from './Item';
import PositionSvg from './position.svg';
import SenioritySvg from './seniority.svg';
import SkillsSvg from './skills.svg';
import SpecialitySvg from './speciality.svg';
import './styles.scss';

@connect(null, { setErrorFlashMessage })
export default class Details extends PureComponent {
  static propTypes = {
    opening: PropTypes.shape({
      employmentType: PropTypes.string,
      id: PropTypes.string,
      isRelocation: PropTypes.bool,
      seniorities: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string.isRequired,
        }).isRequired,
      ),
      skills: PropTypes.arrayOf(
        PropTypes.shape({
          skill: PropTypes.shape({
            id: PropTypes.string.isRequired,
            tags: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
          }).isRequired,
        }).isRequired,
      ),
      speciality: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        skillTags: PropTypes.arrayOf(PropTypes.string.isRequired),
      }),
      targetLocations: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          shortName: PropTypes.string.isRequired,
        }).isRequired,
      ),
    }).isRequired,
    saving: PropTypes.bool.isRequired,
    setErrorFlashMessage: PropTypes.func.isRequired,
  };

  state = {
    loadingReach: false,
    opening: this.props.opening,
    reachAmount: 0,
  };

  _isMounted = false;

  getReach = debounce(async opening => {
    const { setErrorFlashMessage } = this.props;

    if (idify(opening)) {
      try {
        this.setState({ loadingReach: true });
        const {
          data: {
            opening: {
              reach: { local, global, remote },
            },
          },
        } = await apolloClient.query({
          fetchPolicy: 'no-cache',
          query: GetOpeningReachDocument,
          variables: {
            id: idify(opening),
          },
        });

        const { isRemote, isRelocation, targetLocations } = opening;

        const isGlobal = isRelocation || isEmpty(targetLocations);

        this.setState({
          loadingReach: false,
          reachAmount: isGlobal ? global : isRemote ? remote : local,
        });
      } catch (error) {
        setErrorFlashMessage();
      }
    }
  }, 300);

  componentDidMount() {
    this._isMounted = true;
    this.getReach(this.props.opening);
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.saving && !nextProps.saving) {
      this.getReach(nextProps.opening);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  minSkillsFor(speciality) {
    if (!speciality) {
      return 0;
    }

    switch (speciality.name) {
      case 'Software Engineering':
        return 3;
      case 'Data Science':
        return 2;
      default:
        return 0;
    }
  }

  render() {
    const { reachAmount, loadingReach } = this.state;
    const { opening } = this.props;

    const { isRelocation, employmentType, targetLocations, seniorities, speciality: track, skills: _skills } = opening;
    const specialityTag = track && track.skillTags[0];
    const mappedSkills = map(_skills, 'skill');

    const [specialities, skills] = partition(mappedSkills, ({ tags }) => includes(tags, specialityTag));

    return (
      <div className="sidebar-statistics">
        <Reach count={reachAmount} loadingReach={loadingReach} relocation={isRelocation} title="Potential reach" />

        <span className="sidebar-statistics__looking-for">I’m looking for someone</span>

        {!isRelocation ? (
          <Item description="Who is located in" icon={LocationSvg} values={map(targetLocations, 'shortName')} />
        ) : null}

        {track ? <Item description="Who works in" icon={PositionSvg} values={[track.name]} /> : null}

        <Item description="Who specialise in" icon={SpecialitySvg} values={map(specialities, 'name')} />

        {skills.length ? (
          <Item description="And has following skills" icon={SkillsSvg} values={map(skills, 'name')} />
        ) : null}

        {opening ? (
          <Item
            description="We are looking to fill a"
            icon={SenioritySvg}
            suffix="position"
            values={employmentType === 'INTERNSHIP' ? ['Internship'] : map(seniorities, 'name')}
          />
        ) : null}
      </div>
    );
  }
}
